import { supabase } from './supabase';

export interface ProcessingHistoryEntry {
  id: string;
  productId?: string;
  productName: string;
  customizations?: any;
  format: string;
  filePath?: string;
  createdAt: string;
}

export async function getHistory(workspaceId: string): Promise<ProcessingHistoryEntry[]> {
  const { data, error } = await supabase
    .from('processing_history')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => ({
    id: row.id,
    productId: row.product_id || undefined,
    productName: row.product_name,
    customizations: row.customizations || undefined,
    format: row.format,
    filePath: row.file_path || undefined,
    createdAt: row.created_at,
  }));
}

export async function recordDownload(
  workspaceId: string,
  entry: {
    productId?: string;
    productName: string;
    customizations?: any;
    format: string;
  },
  file?: { blob: Blob; filename: string }
): Promise<void> {
  // Upload file to storage first if provided (use a unique path)
  let filePath: string | undefined;
  if (file) {
    const fileId = crypto.randomUUID();
    const ext = file.filename.split('.').pop() || entry.format;
    const storagePath = `${workspaceId}/${fileId}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('downloads')
      .upload(storagePath, file.blob, { contentType: file.blob.type });

    if (uploadError) {
      console.error('[History] Failed to upload file:', uploadError);
    } else {
      filePath = storagePath;
    }
  }

  // Insert history record with file path included
  const { error: historyError } = await supabase
    .from('processing_history')
    .insert({
      workspace_id: workspaceId,
      product_id: entry.productId,
      product_name: entry.productName,
      customizations: entry.customizations,
      format: entry.format,
      file_path: filePath || null,
    });

  if (historyError) throw historyError;

  // Atomically increment download count via RPC
  const period = new Date().toISOString().slice(0, 7);

  const { data: rpcData, error: rpcError } = await supabase.rpc('check_and_increment_download', {
    p_workspace_id: workspaceId,
    p_period: period,
  });

  if (rpcError) throw rpcError;

  // RPC returns { allowed, remaining, current, limit } — if not allowed, the history
  // was already recorded but the download count wasn't incremented
  if (rpcData && !rpcData.allowed) {
    console.warn('[History] Download limit reached:', rpcData);
  }
}

export function getDownloadUrl(filePath: string): string {
  const { data } = supabase.storage.from('downloads').getPublicUrl(filePath);
  return data.publicUrl;
}

export async function getUsage(
  workspaceId: string
): Promise<{ downloadCount: number; period: string }> {
  const period = new Date().toISOString().slice(0, 7);

  const { data, error } = await supabase
    .from('usage_tracking')
    .select('download_count')
    .eq('workspace_id', workspaceId)
    .eq('period', period)
    .maybeSingle();

  if (error) throw error;

  return {
    downloadCount: data?.download_count || 0,
    period,
  };
}
